import { useRef, useState } from 'react';
import { useScriptActionBindingLazy } from '../../../../api/hooks/binding/script-action.binding';
import { useMachineContext } from '../../../../pages/machine/machine-context-provider';
import { logger } from '../../../../util/logger';
import { QNear, Waypoint } from '../../../../util/machine-variable.utils';
import { useFlowServices } from './flow-service.hooks';

export interface MoveToPointProps {
  point?: Waypoint;
  qnear?: QNear;
  tcp?: Waypoint;
  stickout?: number;
  executingIndex?: number;
}

interface MoveToPointResults {
  startMoveToPoint: (props: MoveToPointProps) => Promise<void>;
  isLoading: boolean;
}

const useMoveToPoint = (): MoveToPointResults => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const isMovingRef = useRef<boolean>(false);
  const isSendingStartRef = useRef<boolean>(false);

  const { send: sendScript } = useScriptActionBindingLazy();

  const { flowAnalyticService } = useFlowServices();
  const { machine } = useMachineContext();

  const startMoveToPoint = async (props: MoveToPointProps) => {
    const { point, qnear, tcp, stickout, executingIndex } = props;

    setIsLoading(true);

    if (!isMovingRef.current && point && qnear && tcp) {
      isMovingRef.current = true;
      isSendingStartRef.current = true;

      const script = getMoveScript(
        point,
        qnear,
        stickout ?? 0,
        tcp,
        executingIndex ?? 0
      );

      await sendScript(machine.id, {
        script,
        validProgramStates: ['PROGRAM_STOPPED', 'PROGRAM_PAUSED'],
      });

      isMovingRef.current = false;
      isSendingStartRef.current = false;
      logger.info('move to point - started moving');
      flowAnalyticService.trackMoveToPoint();
    }

    setIsLoading(false);
  };

  return { startMoveToPoint, isLoading };
};

const getMoveScript = (
  point: Waypoint,
  qnear: QNear,
  stickout: number,
  tcp: Waypoint,
  executingIndex: number
): string => {
  const convertedPoint = convertWaypointToScriptPose(point);
  const convertedTcp = convertWaypointToScriptPose(tcp);

  const script = [
    `def move_to_point():`,
    ` local LIVE_EXECUTION_REG = 24`,
    ` local PROGRAM_END = -1`,
    ` local START_DISTANCE_THRESHOLD = 0.0005`,
    ` local THREAD_INACTIVE = -999`,
    ` def moveHere(pose, jointPosition, stickout, tcp, liveExecutionStep):`,
    ` 	set_tcp(tcp)`,
    ` 	pose = pose_trans(pose, p[0, 0, -stickout / 1000, 0, 0, 0])`,
    ` 	setMoveToWaypoint(pose, jointPosition)`,
    ` 	set_standard_digital_out(0, True) #REDUCED MODE FOR SAFETY`,
    ` 	set_standard_digital_out(1, True) #REDUCED MODE FOR SAFETY`,
    ` 	global reachedPoint = False`,
    ` 	local btn_thrd = run flashBlueButton()`,
    ` 	local moveHereThreadID = THREAD_INACTIVE`,
    ` 	write_output_integer_register(LIVE_EXECUTION_REG, liveExecutionStep) # Setting Live Execution step to register`,
    ` 	if (pose_dist(get_actual_tcp_pose(), pose) < START_DISTANCE_THRESHOLD):`,
    ` 		reachedPoint = True`,
    ` 	end`,
    ` 	textmsg("Entering Move Here loop, reached point: ", reachedPoint)`,
    ` 	while (not reachedPoint):`,
    ` 		while (not get_standard_digital_in(1)):`,
    ` 			sync()`,
    ` 			if (get_tool_digital_in(0)):`,
    ` 				textmsg("Operator using Free Drive in Move Here")`,
    ` 				freedrive_mode()`,
    ` 				set_tool_digital_out(0, False)`,
    ` 				while (get_tool_digital_in(0)):`,
    ` 					sync()`,
    ` 				end`,
    ` 				end_freedrive_mode()`,
    ` 				set_tool_digital_out(0, True)`,
    ` 			end`,
    ` 		end`,
    ` 		if (get_standard_digital_in(1) and not reachedPoint):`,
    ` 			textmsg("Operator has pressed blue button to move to point")`,
    ` 			set_standard_digital_out(0, False) #REDUCED MODE FOR SAFETY`,
    ` 			set_standard_digital_out(1, False) #REDUCED MODE FOR SAFETY`,
    ` 			set_tool_digital_out(0, False)`,
    ` 			while (get_standard_digital_in(1) and not reachedPoint):`,
    ` 				if (moveHereThreadID == THREAD_INACTIVE):`,
    ` 					moveHereThreadID = run moveToPointThrd()`,
    ` 				end`,
    ` 				sync()`,
    ` 			end`,
    ` 			if (not get_standard_digital_in(1) or reachedPoint):`,
    ` 				kill moveHereThreadID`,
    ` 				set_tool_digital_out(0, True)`,
    ` 				stopj(5)`,
    ` 				textmsg("Ending move to point")`,
    ` 				moveHereThreadID = THREAD_INACTIVE`,
    ` 				set_standard_digital_out(0, True) #REDUCED MODE FOR SAFETY`,
    ` 				set_standard_digital_out(1, True) #REDUCED MODE FOR SAFETY`,
    ` 				set_tcp(tcp)`,
    ` 				sleep(0.05)`,
    ` 			end`,
    ` 		end`,
    ` 	end`,
    ` 	kill btn_thrd`,
    ` 	set_standard_digital_out(2, False)`,
    ` 	write_output_integer_register(LIVE_EXECUTION_REG, PROGRAM_END)`,
    ` 	halt`,
    ` end`,
    ` thread moveToPointThrd():`,
    ` 	while (True):`,
    ` 		local pose = getMoveToWaypoint()`,
    ` 		local qnear = getMoveToJointPosition()`,
    ` 		movej(get_inverse_kin(pose, qnear), a = d2r(40), v = d2r(20))`,
    ` 		reachedPoint = True`,
    ` 		stopj(5)`,
    ` 	end`,
    ` end`,
    ` thread flashBlueButton():`,
    ` 	while (True):`,
    ` 		set_standard_digital_out(2, False)`,
    ` 		sleep(1)`,
    ` 		set_standard_digital_out(2, True)`,
    ` 		sleep(1)`,
    ` 	end`,
    ` end`,
    ` local moveToPose = p[0, 0, 0, 0, 0, 0]`,
    ` local moveToJointPosition = [0, 0, 0, 0, 0, 0]`,
    ` def setMoveToWaypoint(pose, qnear):`,
    ` 	moveToPose = pose`,
    ` 	moveToJointPosition = qnear`,
    ` end`,
    ` def getMoveToWaypoint():`,
    ` 	return moveToPose`,
    ` end`,
    ` def getMoveToJointPosition():`,
    ` 	return moveToJointPosition`,
    ` end`,
    ` moveHere(${convertedPoint}, [${qnear}], ${stickout}, ${convertedTcp}, ${executingIndex})`,
    `end`,
  ];

  return script.join('\n');
};

const convertWaypointToScriptPose = (waypoint: Waypoint): string => {
  return `p[${[
    waypoint.x,
    waypoint.y,
    waypoint.z,
    waypoint.rx,
    waypoint.ry,
    waypoint.rz,
  ].join(',')}]`;
};

export { useMoveToPoint };
